import type { ShouldRevalidateFunction } from "react-router";

import { isAuthRevalidatingSubmit } from "../auth-revalidate";

/**
 * Search Products/Articles tab helpers (SFCC-11499 / SFCC-11735).
 *
 * The active tab is persisted in the `?tab=` URL param so it is shareable,
 * deep-linkable, and survives reload + browser back/forward. Products is the
 * default and writes NO param (clean canonical URL); only Articles writes
 * `?tab=articles`. Switching tabs is a pure client-side view toggle — it must
 * NOT re-run the search loader (see `searchShouldRevalidate`).
 */

export const SEARCH_TAB_PARAM = "tab";

export type SearchTab = "products" | "articles";

/**
 * Resolve the active tab from the raw `?tab=` value. Defaults to `products`,
 * and defensively falls back to `products` when `articles` is requested but the
 * Articles tab isn't available (e.g. a deep-linked `?tab=articles` on a query
 * that returned no articles) so the user is never stranded on a hidden tab.
 */
export function resolveSearchTab(
  tabParam: string | null,
  showArticlesTab: boolean,
): SearchTab {
  return tabParam === "articles" && showArticlesTab ? "articles" : "products";
}

/** Strip the `?tab=` param from a search string for canonical comparison. */
export function stripTabParam(search: string): string {
  const params = new URLSearchParams(search);
  params.delete(SEARCH_TAB_PARAM);
  return params.toString();
}

/**
 * `shouldRevalidate` for the `/search` route. The route has no other custom
 * revalidation rules, so this only adds ONE exception — a `tab`-only change is
 * a client-side tab switch and keeps the current loader data — and defers every
 * other navigation (filters, sort, query, pagination, forms) to RR7's default.
 *
 * The exception is deliberately narrow, and the guards below matter: the search
 * loader is NOT URL-pure. It reads the session, the AFID/LMN gate, the
 * price-test cookie, and a shopper-scoped SCAPI token, so any refresh that
 * isn't a plain `tab`-only link navigation must reach it. Concretely:
 *
 *   - Non-GET submissions (account-drawer sign-in fetcher POST to `/login`,
 *     logout, account forms, …) revalidate every active loader by design —
 *     swallowing them would leave the grid on guest-scoped SCAPI prices, the
 *     guest AB-pricebook segment, and guest SWR-cached CIO ranking after login.
 *   - EXCEPT basket submissions (COM-830): a `/resources/basket` add / update /
 *     remove changes none of the loader's inputs (session identity, AFID, price
 *     cookie, SCAPI token scope), and basket-driven UI refreshes from
 *     `BasketProvider`'s own fetcher. Re-running the loader re-paid the CIO
 *     search + SCAPI tile enrichment on every add AND reset an accumulated Load
 *     More grid to one page. An auth-revalidating submit still wins (it can
 *     never target `/basket`, but the check keeps the intent explicit).
 *   - Byte-identical URLs reach here from explicit `revalidate()` calls and
 *     post-action refreshes, never from a tab switch (a tab switch always
 *     changes `search`). Defer to the default rather than suppressing them.
 *
 * Mirrors the ordering of the sibling `dispatcherShouldRevalidate`.
 */
export const searchShouldRevalidate: ShouldRevalidateFunction = ({
  formMethod,
  formAction,
  actionStatus,
  currentUrl,
  nextUrl,
  defaultShouldRevalidate,
}) => {
  if (formMethod !== undefined && formMethod !== "GET") {
    if (
      formAction?.includes("/basket") &&
      !isAuthRevalidatingSubmit({ formMethod, formAction, actionStatus })
    ) {
      return false;
    }
    return defaultShouldRevalidate;
  }
  if (currentUrl.pathname !== nextUrl.pathname) return defaultShouldRevalidate;
  if (currentUrl.search === nextUrl.search) return defaultShouldRevalidate;
  if (stripTabParam(currentUrl.search) === stripTabParam(nextUrl.search)) {
    return false;
  }
  return defaultShouldRevalidate;
};
